import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { AiAssistant } from './AiAssistant';
import { LeftRail } from './LeftRail';
import type { RailPanelId } from './LeftRail';
import { RightPanel, attendanceContextOf } from './RightPanel';
import { TopBar } from './TopBar';
import { useKeyboardInset } from './useKeyboardInset';
import { useDraggableChrome } from './useDraggableChrome';
import { ChevronDownIcon, MoveIcon } from './icons';
import { Filmstrip, findGeneratedDocument, useDynamicDocumentStore, useFilmstripStore } from '../board/filmstrip';
import { QuizControls, QuizResultsPanel, useQuizStore } from '../board/quiz';
import { useExamQuizStore } from '../board/quiz/examQuiz';
import { useBoardContext } from '../board/useBoardContext';
import { useEndPeriod } from '../board/useEndPeriod';
import { Modal, Toast } from '../components';
import { useQltAuthStore, useQltSlotAttendanceStore, useQltTimetableStore } from '../stores/qlt';
import { classFallback, periodCell, subjectFallback } from '../board/periodLabels';
import { vietnamDate } from '../home/date';
import { RailPanelHost } from '../board/RailPanelHost';
import { YoutubeOverlay } from '../board/link/YoutubeOverlay';
import type { InsertedYoutubeVideo } from '../board/panels/InsertLinkPanel';
import { UploadOverlay } from '../board/upload/UploadOverlay';
import { uploadViewer } from '../board/upload/validation';
import { useSelectUnit } from '../board/useSelectUnit';
import { BoardResults } from '../board/results/BoardResults';
import { activeSession } from '../board/results/sessions';
import { useResultSessionStore } from '../board/results/store';
import { useResultActions } from '../board/results/useResultActions';
import { useUnitResultsToggle } from '../board/results/useUnitResultsToggle';
import { useIzteachUnitStore } from '../stores/izteach';
import { getStudentEmailDomain } from '../api/environment';
import type { QltUploadFile } from '../api/repository/upload';
import { featureEnabled } from '../config/features';
import type { ContentFilter } from '../config/content-types';
import { AI_MESSAGES } from '../board/ai/constants/index.ts';
import { type Msg } from '../i18n/msg';

// full-screen board chrome; the routed page owns the canvas slot underneath
export function BoardShell() {
  const { t } = useTranslation('layout');
  const context = useBoardContext();
  const endPeriod = useEndPeriod(context);
  // collapsed on entry - the Sổ đầu bài widget is the affordance that opens it
  const [panelOpen, setPanelOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [activeRailId, setActiveRailId] = useState<RailPanelId | undefined>(undefined);
  const [libraryFilter, setLibraryFilter] = useState<ContentFilter | undefined>(undefined);
  const [youtubeVideos, setYoutubeVideos] = useState<InsertedYoutubeVideo[]>([]);
  const [activeYoutubeVideo, setActiveYoutubeVideo] = useState<InsertedYoutubeVideo | null>(null);
  const [activeUpload, setActiveUpload] = useState<QltUploadFile | null>(null);
  // State cho toast thông báo khi thêm câu hỏi từ AI thành công
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<Msg | null>(null);
  const attendanceCounts = useQltSlotAttendanceStore((state) => state.counts);
  const loadAttendance = useQltSlotAttendanceStore((state) => state.load);
  // the strip and the rail are independent toggles - neither one drives the other
  const filmstripOpen = useFilmstripStore((state) => state.open);
  // the answer control belongs to a generated quiz document only; every other unit renders none
  const generatedDocuments = useDynamicDocumentStore((state) => state.items);
  const quizDocument = useMemo(() => {
    const document = findGeneratedDocument(context.unitId);
    return document && document.kind === 'quiz' ? document : null;
  }, [context.unitId, generatedDocuments]);
  // the results panel opens as a floating card and reserves no board width; docking it
  // makes it a shell sibling of the right panel, and only then does the canvas re-fit beside it
  // a test unit gets the answer control only once its keys loaded; Kết quả once its exam is known
  const testAnswers = useExamQuizStore((state) => {
    const entry = context.unitId ? state.entries[context.unitId] : undefined;
    return Boolean(entry && entry.status === 'ready' && entry.answers);
  });
  const testExamId = useExamQuizStore((state) => {
    const entry = context.unitId ? state.entries[context.unitId] : undefined;
    return entry && (entry.status === 'ready' || entry.status === 'random') ? entry.examId || null : null;
  });
  const unitTitle = context.lessonTitle || t('shell.testTitleFallback');
  const testResults = useUnitResultsToggle(context.unitId && testExamId ? { tabId: context.unitId, kind: 'exam', refId: testExamId, title: unitTitle } : null);
  // a homework đề offers its class's submissions the same way, when the build can read them
  const homeworkUnit = useIzteachUnitStore((state) => Boolean(context.unitId && state.renderable && state.renderable.kind === 'homework' && state.unit && String(state.unit.id || state.unit._id || '') === context.unitId));
  const homeworkResultsOn = homeworkUnit && featureEnabled('board.submissions') && Boolean(getStudentEmailDomain());
  const homeworkResults = useUnitResultsToggle(homeworkResultsOn && context.unitId ? { tabId: context.unitId, kind: 'homework', refId: context.unitId, title: context.lessonTitle || t('shell.homeworkTitleFallback') } : null);
  // the đề's rail carries Kết quả and Chi tiết; without it the card switches views itself
  const examRail = Boolean(!quizDocument && testExamId && context.unitId);
  const homeworkRail = Boolean(homeworkResultsOn && context.unitId);
  const quizResultsOpen = useQuizStore((state) => state.resultsOpen);
  const quizResultsDocked = useQuizStore((state) => state.resultsDocked);
  const quizResultsShown = Boolean(quizDocument) && quizResultsOpen;
  const quizResultsPinned = quizResultsShown && quizResultsDocked;
  // only the collapsed widget moves; the expanded right panel is anchored by its own CSS
  const logbookDrag = useDraggableChrome({ id: 'logbook-widget', enabled: !panelOpen });
  useKeyboardInset();
  const selectUnit = useSelectUnit();
  const results = useResultActions(context);
  // a homework student's work replaces the đề, with the navigator bar over the board
  const studentShown = useResultSessionStore((state) => {
    const session = activeSession(state.sessions, context.unitId);
    return Boolean(session && session.kind === 'homework' && session.student);
  });

  // an image or PDF becomes a board document, so the pen writes on it and the ink is kept per file
  const openUpload = (file: QltUploadFile) => {
    const viewer = uploadViewer(file);
    if (viewer !== 'image' && viewer !== 'pdf') {
      setActiveUpload(file);
      return;
    }
    const document = useDynamicDocumentStore.getState().openUpload(file, viewer);
    setActiveUpload(null);
    setActiveRailId(undefined);
    selectUnit({ unitId: document.id, unitName: document.title });
  };

  // the collapsed widget shows Sĩ số, so the roster loads here rather than inside the panel
  const attendanceContext = useMemo(() => attendanceContextOf(context), [context.classId, context.date, context.index]);
  useEffect(() => { void loadAttendance(attendanceContext); }, [attendanceContext, loadAttendance]);

  // labels and the merged-block range read today's timetable, so load it when this period is missing
  const teacherId = useQltAuthStore((state) => state.teacherId);
  const loadToday = useQltTimetableStore((state) => state.loadToday);
  const periodKnown = useQltTimetableStore((state) => periodCell(state.todayCells, context) !== null);
  useEffect(() => {
    if (periodKnown || !teacherId || context.date !== vietnamDate()) return;
    void loadToday(teacherId, context.date);
  }, [periodKnown, context.date, teacherId, loadToday]);

  const shellClass = [
    'shell',
    panelOpen ? '' : 'shell--panel-collapsed',
    activeRailId ? 'shell--rail-panel' : '',
    filmstripOpen ? 'shell--filmstrip' : '',
    quizResultsPinned ? 'shell--quiz-results' : '',
    studentShown ? 'shell--submission' : '',
  ].filter(Boolean).join(' ');

  /**
   * Báo kết quả import câu hỏi từ AI Quiz Supporter.
   *
   * Khi bộ câu hỏi đã được mở thành tài liệu trên bảng thì bảng chính là đích đến, nên panel kho
   * bài KHÔNG mở đè lên nữa - đó chính là lỗi được báo. Chỉ khi không mở được lên bảng mới quay
   * về hành vi cũ: mở panel kho bài để thầy/cô tìm lại câu hỏi.
   *
   * @param highlightIds - Danh sách ID các câu hỏi cần highlight
   * @param openedOnBoard - Bộ câu hỏi đã mở thành tài liệu trên bảng hay chưa
   */
  const handleOpenLibrary = (highlightIds: string[], openedOnBoard = false) => {
    if (openedOnBoard) {
      setToastMessage(AI_MESSAGES.QUIZ_IMPORT_ON_BOARD(highlightIds.length));
    } else {
      // chỉ khi không mở được lên bảng mới quay về kho bài - lọc sẵn BKT để thấy bộ vừa tạo
      setLibraryFilter('assessment');
      setActiveRailId('library');
      setToastMessage(AI_MESSAGES.QUIZ_IMPORT_SUCCESS(highlightIds.length));
    }
    setToastOpen(true);
  };

  return (
    <div className={shellClass}>
      <TopBar className={context.classTitle || classFallback()} lessonTitle={context.lessonTitle || context.subjectTitle || t('topbar.lessonFallback')} date={context.date} index={context.index} onClose={() => setEndOpen(true)} />

      <div className="shell__body">
        <main className="shell__canvas">
          <Suspense fallback={<div className="shell__loading">{t('shell.loading')}</div>}>
            <Outlet />
          </Suspense>
        </main>

        {/* the + tile opens Kho bài */}
        {filmstripOpen ? <Filmstrip activeId={context.unitId} activeTitle={context.lessonTitle} onAdd={() => { setLibraryFilter(undefined); setActiveRailId('library'); }} /> : null}

        {quizDocument ? <QuizControls documentId={quizDocument.id} /> : null}
        {examRail && context.unitId ? <QuizControls documentId={context.unitId} answers={testAnswers} results={testResults.report} detail={testResults.detail} /> : null}
        {homeworkRail && context.unitId ? <QuizControls documentId={context.unitId} answers={false} results={homeworkResults.report} detail={homeworkResults.detail} /> : null}

        <div className="shell__left">
          <LeftRail docked={Boolean(activeRailId)} activeId={activeRailId} onSelect={(id) => { if (id === 'library') setLibraryFilter(undefined); setActiveRailId(activeRailId === id ? undefined : id); }} />
          {activeRailId ? <RailPanelHost activeId={activeRailId} context={context} videos={youtubeVideos} libraryFilter={libraryFilter} onInsertVideo={(video) => { setYoutubeVideos((current) => [video].concat(current.filter((item) => item.id !== video.id || item.start !== video.start))); setActiveYoutubeVideo(video); }} onPlayVideo={setActiveYoutubeVideo} onOpenUpload={openUpload} onOpenTask={(task, courseTitle) => { results.openHomework(task, courseTitle); setActiveRailId(undefined); }} onOpenExam={(examId) => { if (results.openExam(examId)) setActiveRailId(undefined); }} onSelectSuggestion={(selection) => { selectUnit(selection); setActiveRailId(undefined); }} onClose={() => { setActiveRailId(undefined); setLibraryFilter(undefined); }} /> : null}
          {featureEnabled('board.ai') ? <AiAssistant context={context} onOpenLibrary={handleOpenLibrary} /> : null}
        </div>

        {panelOpen ? <RightPanel context={context} onCollapse={() => setPanelOpen(false)} /> : (
          <div ref={logbookDrag.ref} className={logbookDrag.dragging ? 'logbook-widget logbook-widget--dragging' : 'logbook-widget'}>
            <button
              type="button"
              className="logbook-widget__handle"
              aria-label={t('logbook.dragHandle')}
              {...logbookDrag.handleProps}
            >
              <MoveIcon size={18} />
            </button>
            {/* covers the whole face so tapping the card still expands it */}
            <button type="button" className="logbook-widget__open" aria-label={t('logbook.open')} aria-expanded={false} onClick={() => setPanelOpen(true)} />
            <span className="logbook-widget__title">{t('panel.title')}</span>
            <span className="logbook-widget__subtitle">{context.periodLabel} • {context.subjectTitle || subjectFallback()}</span>
            <span className="logbook-widget__mini">
              <span className="logbook-widget__mini-label">{t('attendance.classSize')}</span>
              <span className="logbook-widget__mini-value">{t('logbook.studentsValue', { present: attendanceCounts.present, total: attendanceCounts.total })}</span>
            </span>
            <span className="logbook-widget__caret" aria-hidden={true}><ChevronDownIcon size={16} /></span>
          </div>
        )}

        {quizDocument && quizResultsShown ? <QuizResultsPanel document={quizDocument} /> : null}

        <YoutubeOverlay video={activeYoutubeVideo} onClose={() => setActiveYoutubeVideo(null)} />
        <UploadOverlay file={activeUpload} onClose={() => setActiveUpload(null)} />
        <BoardResults context={context} actions={results} railShown={examRail || homeworkRail} />
      </div>
      <Modal open={endOpen} title={t('endLesson.title')} description={t('endLesson.description')} onClose={() => setEndOpen(false)} actions={<><button type="button" className="secondary-button" onClick={() => setEndOpen(false)}>{t('endLesson.keepTeaching')}</button><button type="button" className="primary-button" onClick={endPeriod}>{t('endLesson.confirm')}</button></>}>
        <p className="end-period-copy">{t('endLesson.copy')}</p>
      </Modal>
      {/* Toast thông báo khi thêm câu hỏi từ AI Quiz Supporter thành công */}
      <Toast open={toastOpen} kind="success" message={toastMessage ?? ''} onClose={() => setToastOpen(false)} />
    </div>
  );
}
